<script lang="ts">
  import { navigate } from '../lib/router.svelte';
  import AdminShell from './AdminShell.svelte';
  import PageContainer from '../lib/ui/PageContainer.svelte';
  import StateBlock from '@ui/StateBlock.svelte';
  import PageHeader from '../lib/ui/PageHeader.svelte';
  import Avatar from '@ui/Avatar.svelte';
  import {
    redirectIfAwaitingOnboarding,
    getToken,
    handle401,
    ApiError,
    type AdminUser,
  } from '../lib/admin';
  import { ensureCommunities } from '../lib/community.svelte';
  import { asAdminUser, ensureBootstrap } from '../lib/session';
  import { createAffiliate, createPayout, fetchAccounts, fetchAllAffiliates, patchAffiliate, type Account, type Affiliate } from '@api';
  import { fmtUsd, fmtRate, referralLink } from '../lib/affiliates';
  import CopyButton from '../lib/ui/CopyButton.svelte';

  let me = $state<AdminUser | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let rows = $state<Affiliate[]>([]);

  // Small transient confirmation (mark-paid result, 409, etc.).
  let toast = $state<string | null>(null);
  let toastTimer: ReturnType<typeof setTimeout> | undefined;
  function flash(msg: string): void {
    toast = msg;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => (toast = null), 3200);
  }

  // Per-row busy set (mark-paid / enable-disable).
  let busy = $state<Set<number>>(new Set());

  // Inline rate edit (percent in the box; converted to bps on save).
  let editingRate = $state<number | null>(null);
  let rateDraft = $state('');
  let rateSaving = $state<number | null>(null);
  let rateError = $state<string | null>(null);

  // Add-affiliate flow — the picker searches platform ACCOUNTS, not community
  // members, since affiliates are accounts.
  //
  // The search is SERVER-SIDE now (`GET /v1/accounts?q=&fields=name&fields=email`).
  // It used to download every account on the platform, with each one's communities and
  // plan state, at page load, and run a typeahead over them in the browser
  // (`02-admin-consumption.md` §5.4) — for a dropdown that shows eight rows of two
  // fields and that most visits never open.
  let accountResults = $state<Account[]>([]);
  let accountsLoading = $state(false);
  let addSearch = $state('');
  let addOpen = $state(false);
  let addPicked = $state<Account | null>(null);
  let addCode = $state('');
  // `type="number"` binds this as number | null (null when the field is empty) —
  // never a string, so don't call string methods on it.
  let addRate = $state<number | null>(null);
  let addBusy = $state(false);
  let addError = $state<string | null>(null);

  const existingIds = $derived(new Set(rows.map((r) => r.user_id)));

  // Candidate accounts: whatever the server matched, minus the ones already enrolled.
  const accountMatches = $derived(
    accountResults.filter((a) => !existingIds.has(a.user_id)).slice(0, 8),
  );

  let searchTimer: ReturnType<typeof setTimeout> | undefined;
  function onAccountSearch(): void {
    addOpen = true;
    clearTimeout(searchTimer);
    const term = addSearch.trim();
    if (!term) {
      accountResults = [];
      accountsLoading = false;
      return;
    }
    accountsLoading = true;
    searchTimer = setTimeout(() => void searchAccounts(term), 250);
  }

  async function searchAccounts(term: string): Promise<void> {
    accountsLoading = true;
    try {
      // Eight rows of two fields — `?fields=` is what makes that literally true on
      // the wire, rather than the full account row for every match.
      accountResults = (
        await fetchAccounts({ q: term, limit: 20, fields: ['name', 'email'] })
      ).items;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      accountResults = [];
    } finally {
      accountsLoading = false;
    }
  }

  /**
   * ONE round trip (doc 04 §4.1), and one request instead of two: the accounts list
   * is no longer fetched at all until somebody types in the picker.
   *
   * The roster request is issued before the role is known and its failure is held
   * rather than thrown — `/v1/affiliates` is superadmin-only, so a creator who lands
   * here spends one swallowed 403 on the way to the redirect below.
   */
  async function init(): Promise<void> {
    if (!getToken()) {
      navigate('/admin/login');
      return;
    }
    loading = true;
    error = null;
    let pageErr: unknown = null;
    const pending = fetchAllAffiliates().catch((err: unknown) => {
      pageErr = err;
      return null;
    });
    try {
      me = asAdminUser(await ensureBootstrap());
      if (redirectIfAwaitingOnboarding(me, navigate)) return;
      if (me.role !== 'superadmin') {
        navigate('/admin');
        return;
      }
      await ensureCommunities();
      const loaded = await pending;
      if (loaded) rows = loaded.items;
      else if (pageErr) throw pageErr;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      error = err instanceof Error ? err.message : 'Failed to load.';
    } finally {
      loading = false;
    }
  }
  void init();

  /** Patch one console row from a write's response — every affiliate mutation returns
   *  the updated row, so nothing here re-reads the table (doc 02 W9). */
  function patchRow(updated: Affiliate): void {
    rows = rows.map((x) => (x.user_id === updated.user_id ? updated : x));
  }

  // --- inline rate edit -------------------------------------------------------
  function startEditRate(r: Affiliate): void {
    editingRate = r.user_id;
    rateDraft = String(r.rate_bps / 100);
    rateError = null;
  }
  function cancelEditRate(): void {
    editingRate = null;
    rateError = null;
  }
  async function saveRate(r: Affiliate): Promise<void> {
    const raw = rateDraft.trim();
    // An empty box clears the special deal back to the program default.
    let patch: { rate_bps: number | null };
    if (raw === '') {
      patch = { rate_bps: null };
    } else {
      const pct = Number(raw);
      if (Number.isNaN(pct) || pct < 0 || pct > 100) {
        rateError = 'Enter a percent between 0 and 100.';
        return;
      }
      patch = { rate_bps: Math.round(pct * 100) };
    }
    rateSaving = r.user_id;
    rateError = null;
    try {
      patchRow(await patchAffiliate(r.user_id, patch));
      editingRate = null;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      rateError = err instanceof Error ? err.message : 'Could not update the rate.';
    } finally {
      rateSaving = null;
    }
  }

  // --- row actions ------------------------------------------------------------

  /** Record a manual payout. `POST …/payouts` returns the amount, the row count AND
   *  the updated console row, so the whole table is no longer re-read to show one
   *  changed balance (doc 02 W9 — this was the last of the three chains on this page,
   *  the other two already patched locally). */
  async function markPaid(r: Affiliate): Promise<void> {
    if (r.totals.payable_cents <= 0 || busy.has(r.user_id)) return;
    if (
      !confirm(
        `Mark ${fmtUsd(r.totals.payable_cents)} paid to ${r.name}? This stamps every payable commission paid and records the payout.`,
      )
    ) {
      return;
    }
    busy = new Set(busy).add(r.user_id);
    try {
      const res = await createPayout(r.user_id);
      patchRow(res.affiliate);
      flash(`Marked ${fmtUsd(res.amount_cents)} paid to ${r.name}.`);
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      if (err instanceof ApiError && err.status === 409) flash('Nothing payable.');
      else flash(err instanceof Error ? err.message : 'Could not mark paid.');
    } finally {
      const n = new Set(busy);
      n.delete(r.user_id);
      busy = n;
    }
  }

  async function toggleDisabled(r: Affiliate): Promise<void> {
    if (busy.has(r.user_id)) return;
    busy = new Set(busy).add(r.user_id);
    try {
      patchRow(await patchAffiliate(r.user_id, { disabled: !r.disabled }));
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      flash(err instanceof Error ? err.message : 'Could not update.');
    } finally {
      const n = new Set(busy);
      n.delete(r.user_id);
      busy = n;
    }
  }

  // --- add affiliate ----------------------------------------------------------
  function pickAccount(a: Account): void {
    addPicked = a;
    addOpen = false;
    addSearch = '';
    accountResults = [];
    addError = null;
  }

  async function submitAdd(): Promise<void> {
    if (!addPicked || addBusy) return;
    let rateBps: number | undefined;
    if (addRate !== null) {
      if (Number.isNaN(addRate) || addRate < 0 || addRate > 100) {
        addError = 'Enter a rate between 0 and 100%.';
        return;
      }
      rateBps = Math.round(addRate * 100);
    }
    addBusy = true;
    addError = null;
    try {
      const code = addCode.trim();
      const row = await createAffiliate(addPicked.user_id, {
        ...(code ? { code } : {}),
        ...(rateBps != null ? { rateBps } : {}),
      });
      rows = [row, ...rows];
      addPicked = null;
      addCode = '';
      addRate = null;
      flash(`${row.name} is now an affiliate.`);
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      if (err instanceof ApiError && err.status === 409) addError = 'That account is already an affiliate.';
      else if (err instanceof ApiError && err.status === 422)
        addError = 'That code is taken or invalid — try another.';
      else addError = err instanceof Error ? err.message : 'Could not add the affiliate.';
    } finally {
      addBusy = false;
    }
  }
</script>

<AdminShell {me}>
  {#if loading}
    <StateBlock state="loading" />
  {:else if error}
    <StateBlock state="error" message={error} onretry={init} />
  {:else}
    <PageContainer>
      <!-- The eyebrow said "Sideguide AI · Superadmin". This page has nothing to do
           with the Sideguide AI section — that prefix was copied from a neighbour. -->
      <PageHeader section="Super admin" title="Affiliates" />

      <!-- Add an affiliate -->
      <section class="rounded-box border hair bg-base-100 p-5 sm:p-6">
        <div class="eyebrow">Add affiliate</div>
        <h2 class="mt-1 font-display text-xl font-semibold">Enable an account</h2>
        <p class="mt-1.5 max-w-xl text-sm text-base-content/72">
          Affiliate enrollment is invite-only. Pick a creator account, then optionally set a custom
          code and a special rate — leave them blank for a name-based code at the default rate.
        </p>

        <div class="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
          <!-- User picker over the accounts list -->
          <div class="relative flex-1">
            <span class="eyebrow mb-1.5 block text-[0.6rem]">Account</span>
            {#if addPicked}
              <div class="flex items-center gap-2 rounded-field border hair bg-base-100/60 px-2.5 py-1.5">
                <Avatar name={addPicked.name ?? ''} size={24} />
                <div class="min-w-0 flex-1">
                  <div class="redact truncate text-sm font-medium">{addPicked.name}</div>
                  <div class="redact truncate font-mono text-[0.65rem] text-base-content/65">{addPicked.email}</div>
                </div>
                <button
                  class="btn btn-ghost btn-xs btn-circle text-base-content/62 hover:text-error"
                  onclick={() => (addPicked = null)}
                  aria-label="Clear selected account"
                >
                  <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>
              </div>
            {:else}
              <input
                type="search"
                class="input input-bordered input-sm w-full bg-base-100"
                placeholder="Search accounts by name or email"
                bind:value={addSearch}
                onfocus={() => (addOpen = true)}
                oninput={onAccountSearch}
                autocomplete="off"
              />
              {#if addOpen && addSearch.trim()}
                <div class="absolute left-0 right-0 z-30 mt-1 overflow-hidden rounded-field border hair bg-base-100 shadow-xl">
                  {#if accountsLoading && accountMatches.length === 0}
                    <p class="px-3 py-3 text-sm text-base-content/68">Searching…</p>
                  {:else if accountMatches.length === 0}
                    <p class="px-3 py-3 text-sm text-base-content/68">No matching accounts.</p>
                  {:else}
                    <ul class="max-h-64 overflow-y-auto divide-y divide-base-content/5">
                      {#each accountMatches as a (a.user_id)}
                        <li>
                          <button
                            class="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-base-200/50"
                            onclick={() => pickAccount(a)}
                          >
                            <Avatar name={a.name ?? ''} size={26} />
                            <span class="min-w-0 flex-1">
                              <span class="redact block truncate text-sm font-medium">{a.name}</span>
                              <span class="redact block truncate font-mono text-[0.65rem] text-base-content/65">{a.email}</span>
                            </span>
                          </button>
                        </li>
                      {/each}
                    </ul>
                  {/if}
                </div>
              {/if}
            {/if}
          </div>

          <label class="block sm:w-40">
            <span class="eyebrow mb-1.5 block text-[0.6rem]">Code (optional)</span>
            <input
              type="text"
              class="input input-bordered input-sm w-full bg-base-100 font-mono text-sm"
              placeholder="jane"
              bind:value={addCode}
              disabled={!addPicked}
            />
          </label>

          <label class="block sm:w-28">
            <span class="eyebrow mb-1.5 block text-[0.6rem]">Rate %</span>
            <input
              type="number"
              min="0"
              max="100"
              step="0.5"
              class="input input-bordered input-sm w-full bg-base-100 tabular-nums"
              placeholder="30"
              bind:value={addRate}
              disabled={!addPicked}
            />
          </label>

          <button class="btn btn-primary btn-sm gap-2" onclick={submitAdd} disabled={!addPicked || addBusy}>
            {#if addBusy}<span class="loading loading-spinner loading-xs"></span>{/if}
            Add affiliate
          </button>
        </div>

        {#if addError}
          <div class="mt-3 rounded-field border border-error/30 bg-error/10 px-3 py-2 text-sm text-error">
            {addError}
          </div>
        {/if}
      </section>

      <!-- Affiliate roster -->
      <section class="mt-6 rounded-box border hair bg-base-100 p-5 sm:p-6">
        <div class="eyebrow">Affiliates{rows.length ? ` · ${rows.length}` : ''}</div>
        <div class="mt-3 overflow-x-auto">
          <table class="table table-sm">
            <thead>
              <tr class="text-[0.65rem] uppercase tracking-wide text-base-content/65">
                <th>Affiliate</th>
                <th>Code</th>
                <th>Rate</th>
                <th class="text-right">Referrals</th>
                <th class="text-right">Pending</th>
                <th class="text-right">Payable</th>
                <th class="text-right">Paid</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {#if rows.length === 0}
                <tr><td colspan="8" class="py-4 text-sm text-base-content/68">No affiliates yet.</td></tr>
              {:else}
                {#each rows as r (r.user_id)}
                  <tr class={r.disabled ? 'opacity-60' : ''}>
                    <td>
                      <div class="flex items-center gap-2">
                        <Avatar name={r.name} size={30} />
                        <div class="min-w-0">
                          <div class="truncate text-sm font-medium">
                            <span class="redact">{r.name}</span>
                            {#if r.disabled}<span class="ml-1 rounded bg-base-content/10 px-1.5 py-px font-mono text-[0.6rem] uppercase tracking-wide text-base-content/62">Paused</span>{/if}
                          </div>
                          <div class="redact truncate font-mono text-[0.65rem] text-base-content/65">{r.email}</div>
                        </div>
                      </div>
                    </td>
                    <!-- The code is the identifier you READ; the link is what you
                         SEND. Showing only the code meant knowing the `?via=`
                         convention and typing the URL by hand every time an affiliate
                         asked for theirs. Both, in one cell, one press. -->
                    <td>
                      <div class="flex items-center gap-1">
                        <span class="font-mono text-xs">{r.code ?? '—'}</span>
                        <!-- No code, no link, no button. `Affiliate.code` is nullable
                             and a copy control over nothing is worse than an absent
                             one — it would put an empty string on the clipboard and
                             report success. -->
                        {#if r.code}
                          <CopyButton value={referralLink(r.code)}
                                      title="Copy {r.name}’s referral link — {referralLink(r.code)}" />
                        {/if}
                      </div>
                    </td>
                    <td>
                      {#if editingRate === r.user_id}
                        <div class="flex items-center gap-1">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.5"
                            class="input input-bordered input-xs w-16 bg-base-100 tabular-nums"
                            bind:value={rateDraft}
                            placeholder="default"
                            aria-label={`Rate percent for ${r.name}`}
                          />
                          <button
                            class="btn btn-primary btn-xs"
                            onclick={() => saveRate(r)}
                            disabled={rateSaving === r.user_id}
                          >
                            {#if rateSaving === r.user_id}<span class="loading loading-spinner loading-xs"></span>{:else}Save{/if}
                          </button>
                          <button class="btn btn-ghost btn-xs" onclick={cancelEditRate} disabled={rateSaving === r.user_id}>Cancel</button>
                        </div>
                        {#if rateError}<div class="mt-1 text-[0.65rem] text-error">{rateError}</div>{/if}
                      {:else}
                        <button
                          class="inline-flex items-center gap-1 rounded px-1 py-0.5 tabular-nums hover:bg-base-200/60"
                          onclick={() => startEditRate(r)}
                          title="Edit rate"
                        >
                          <span class="text-sm font-medium">{fmtRate(r.rate_bps)}</span>
                          {#if r.has_custom_rate}<span class="rounded bg-primary/10 px-1 py-px font-mono text-[0.55rem] uppercase tracking-wide text-primary/90">custom</span>{/if}
                        </button>
                      {/if}
                    </td>
                    <td class="text-right tabular-nums text-sm">{r.referred_count}</td>
                    <td class="redact text-right font-mono tabular-nums text-sm text-base-content/72">{fmtUsd(r.totals.pending_cents)}</td>
                    <td class="redact text-right font-mono tabular-nums text-sm {r.totals.payable_cents > 0 ? 'text-success' : 'text-base-content/72'}">{fmtUsd(r.totals.payable_cents)}</td>
                    <td class="redact text-right font-mono tabular-nums text-sm text-base-content/72">{fmtUsd(r.totals.paid_cents)}</td>
                    <td>
                      <div class="flex items-center justify-end gap-1.5">
                        <!-- privacy-ok: gates the button; the amount is not rendered on that line -->
                        <button
                          class="btn btn-xs btn-primary"
                          onclick={() => markPaid(r)}
                          disabled={r.totals.payable_cents <= 0 || busy.has(r.user_id)}
                          title={r.totals.payable_cents <= 0 ? 'Nothing payable' : `Mark ${fmtUsd(r.totals.payable_cents)} paid`}
                        >
                          {#if busy.has(r.user_id)}<span class="loading loading-spinner loading-xs"></span>{/if}
                          Mark paid <span class="redact">{fmtUsd(r.totals.payable_cents)}</span>
                        </button>
                        <button
                          class="btn btn-xs btn-ghost {r.disabled ? 'text-success' : 'text-error'}"
                          onclick={() => toggleDisabled(r)}
                          disabled={busy.has(r.user_id)}
                        >
                          {r.disabled ? 'Enable code' : 'Disable code'}
                        </button>
                      </div>
                    </td>
                  </tr>
                {/each}
              {/if}
            </tbody>
          </table>
        </div>
      </section>
    </PageContainer>
  {/if}

  {#if toast}
    <div class="fixed bottom-4 left-1/2 z-[60] -translate-x-1/2 rounded-box border hair bg-base-100 px-4 py-2 text-sm shadow-xl">
      {toast}
    </div>
  {/if}
</AdminShell>
